// packages/frontend/src/services/firebase.ts

import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, Firestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, FirebaseStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, Functions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAnalytics, Analytics, isSupported } from 'firebase/analytics';
import { logger } from '../utils/logger';
import { getRouteTemplate } from '../utils/routeTemplate';

// Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'dummy-api-key',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'mysensorybook-dev.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'mysensorybook-dev',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'mysensorybook-dev.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '123456789',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:123456789:web:abcdef123456',
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || 'G-XXXXXXXXXX',
};

// Validate required configuration only in production
if (import.meta.env.PROD) {
  const requiredEnvVars = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_FIREBASE_PROJECT_ID',
    'VITE_FIREBASE_STORAGE_BUCKET',
    'VITE_FIREBASE_MESSAGING_SENDER_ID',
    'VITE_FIREBASE_APP_ID',
  ];

  const missingVars = requiredEnvVars.filter(
    (varName) => !import.meta.env[varName]
  );

  if (missingVars.length > 0) {
    throw new Error(
      `Missing required Firebase environment variables: ${missingVars.join(', ')}`
    );
  }
}

// Initialize Firebase
let app: FirebaseApp;
let auth: Auth;
let firestore: Firestore;
let storage: FirebaseStorage;
let functions: Functions;
let analytics: Analytics | null = null;

try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  firestore = getFirestore(app);
  storage = getStorage(app);
  functions = getFunctions(app, 'us-central1');

  // Connect to emulators when explicitly enabled
  const useEmulators = import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';

  logger.info('Firebase Emulator Debug:', {
    MODE: import.meta.env.MODE,
    VITE_APP_ENVIRONMENT: import.meta.env.VITE_APP_ENVIRONMENT,
    VITE_USE_FIREBASE_EMULATORS: import.meta.env.VITE_USE_FIREBASE_EMULATORS,
    useEmulators
  });

  if (useEmulators) {
      try {
        // Auth emulator
        logger.info('Attempting to connect to Firebase Auth Emulator...');
        if (!auth.emulatorConfig) {
          connectAuthEmulator(auth, 'http://localhost:9099');
          logger.info('Connected to Firebase Auth Emulator');
        } else {
          logger.info('Firebase Auth Emulator already connected');
        }
        
        // Firestore emulator
        try {
          connectFirestoreEmulator(firestore, 'localhost', 8090);
          logger.info('Connected to Firebase Firestore Emulator on port 8090');
          // Verify connection by checking settings
          const firestoreSettings = (firestore as any)._delegate?._settings;
          if (firestoreSettings?.host?.includes('localhost') || firestoreSettings?.host?.includes('127.0.0.1')) {
            logger.info('Firestore emulator connection verified:', firestoreSettings.host);
          } else {
            logger.warn('⚠ Firestore emulator connection may have failed - settings:', firestoreSettings);
          }
        } catch (error: any) {
          // Emulator already connected or not available
          if (error?.message?.includes('already connected')) {
            logger.info('Firestore emulator already connected');
          } else {
            logger.error('Failed to connect to Firestore emulator:', error);
            logger.error('Make sure Firestore emulator is running on port 8090');
            logger.error('Run: firebase emulators:start');
          }
        }
        
        // Storage emulator
        try {
          connectStorageEmulator(storage, 'localhost', 9199);
          logger.info('Connected to Firebase Storage Emulator');
        } catch (error) {
          // Emulator already connected or not available
          logger.info('Storage emulator connection skipped:', error);
        }
        
        // Functions emulator
        try {
          connectFunctionsEmulator(functions, 'localhost', 5001);
          logger.info('Connected to Firebase Functions Emulator');
        } catch (error) {
          // Emulator already connected or not available
          logger.info('Functions emulator connection skipped:', error);
        }

        // Final verification: Check if Firestore is actually using emulator
        const firestoreSettings = (firestore as any)._delegate?._settings;
        const isUsingEmulator = firestoreSettings?.host?.includes('localhost') || 
                                firestoreSettings?.host?.includes('127.0.0.1');
        
        if (isUsingEmulator) {
          logger.info('Firestore is using emulator:', firestoreSettings.host);
        logger.info('Firebase emulators connected successfully');
        } else {
          logger.warn('⚠ WARNING: Firestore is NOT using emulator!');
          logger.warn('⚠ Firestore settings:', firestoreSettings);
          logger.warn('⚠ This means data will be saved to PRODUCTION Firestore!');
          logger.warn('Make sure VITE_USE_FIREBASE_EMULATORS=true and restart dev server');
        }
      } catch (emulatorError) {
        logger.warn('⚠ Failed to connect to Firebase emulators:', emulatorError);
        logger.info('Make sure emulators are running: firebase emulators:start');
      }
    } else {
      logger.info('Using deployed Firebase services (not emulators)');
      logger.warn('⚠ To use emulators, set VITE_USE_FIREBASE_EMULATORS=true in .env.local');
    }
} catch (error) {
  logger.error('Failed to initialize Firebase:', error);
  throw new Error('Firebase initialization failed');
}

// Firebase Analytics is never started at module load: calling getAnalytics()
// begins GA4's automatic collection immediately, before the user has made a
// consent choice. Callers must invoke initAnalytics() only after consent is
// granted (see services/analyticsService.ts).
let analyticsInitPromise: Promise<Analytics | null> | null = null;

export function initAnalytics(): Promise<Analytics | null> {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }

  if (!analyticsInitPromise) {
    analyticsInitPromise = isSupported()
      .then((supported) => {
        if (!supported) {
          logger.info('Firebase Analytics not supported in this environment');
          return null;
        }
        // gtag attaches the page's dl/dp (document location/path) to EVERY
        // hit it sends, including automatic ones (session_start, engagement),
        // independent of any event's own parameters. Left alone it reports
        // window.location.href, ids and all. Seed it with the route template
        // at init, and analyticsService keeps it current via
        // setDefaultEventParameters as the SPA navigates. Also disable the
        // automatic page_view (send_page_view defaults to true): the app
        // sends its own templated page_view explicitly.
        const routeTemplate = getRouteTemplate(window.location.pathname);
        analytics = initializeAnalytics(app, {
          config: {
            send_page_view: false,
            page_location: `${window.location.origin}${routeTemplate}`,
            page_path: routeTemplate,
          },
        });
        logger.info('Firebase Analytics initialized');
        return analytics;
      })
      .catch((error) => {
        logger.warn('⚠ Failed to initialize Firebase Analytics:', error);
        return null;
      });
  }

  return analyticsInitPromise;
}

// Export Firebase services
export { app, auth, firestore, storage, functions, analytics };
export default app;
